/**
 * genericWorkbook.js
 * Professional Excel builder for catalog/maintenance modules.
 * Works for: Almacenes, Auxiliares, Categorias, Colores, CondicionPago,
 *            Conversiones, Marcas, Modelos, Productos, Subcategorias,
 *            Tallas, Unidades, Usuarios, Series, Sucursales, etc.
 *
 * Usage:
 *   import { exportGenericWorkbook } from '../utils/excel/genericWorkbook';
 *   await exportGenericWorkbook({
 *     titulo: 'REPORTE - ALMACENES',
 *     sheetName: 'Almacenes',
 *     tableName: 'tblAlmacenes',
 *     fileName: 'Almacenes',
 *     columns: [{ key: 'id', label: 'ID', width: 8 }, ...],
 *     rows: filteredData,
 *     searchText,
 *     usuario
 *   });
 */

import { saveAs } from 'file-saver';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ── Style helpers ─────────────────────────────────────────────────────────────

function setThinBorder(cell, color = 'FFD0D7DE') {
  cell.border = {
    top: { style: 'thin', color: { argb: color } },
    left: { style: 'thin', color: { argb: color } },
    bottom: { style: 'thin', color: { argb: color } },
    right: { style: 'thin', color: { argb: color } }
  };
}

function styleHeaderRow(row) {
  row.height = 24;
  row.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    setThinBorder(cell, 'FF123247');
  });
}

function styleBodyRow(row, rowIndex) {
  row.eachCell((cell) => {
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: rowIndex % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF' }
    };
    setThinBorder(cell);
  });
}

function colorEstado(cell) {
  const val = String(cell.value ?? '').trim().toUpperCase();
  if (val === 'ACTIVO' || val === 'SÍ' || val === 'SI') {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
    cell.font = { bold: true, color: { argb: 'FF1B5E20' } };
  } else if (val === 'INACTIVO' || val === 'NO') {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    cell.font = { bold: true, color: { argb: 'FF344054' } };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function colLetter(n) {
  return n <= 26 ? String.fromCharCode(64 + n) : 'A' + String.fromCharCode(64 + n - 26);
}

// ── KPI tiles ─────────────────────────────────────────────────────────────────

function addKpi(ws, labelRange, valueRange, label, value, fill) {
  ws.mergeCells(labelRange);
  const lc = ws.getCell(labelRange.split(':')[0]);
  lc.value = label;
  lc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  lc.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  lc.alignment = { vertical: 'middle', horizontal: 'center' };
  setThinBorder(lc, fill);

  ws.mergeCells(valueRange);
  const vc = ws.getCell(valueRange.split(':')[0]);
  vc.value = value;
  vc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7FAFC' } };
  vc.font = { bold: true, size: 16, color: { argb: 'FF123247' } };
  vc.alignment = { vertical: 'middle', horizontal: 'center' };
  setThinBorder(vc, 'FFD7E3EC');
}

// ── Main sheet ────────────────────────────────────────────────────────────────

function createMainSheet(workbook, opts) {
  const { titulo, sheetName, columns, rows, searchText, usuario } = opts;

  const totalCols = columns.length;
  const lastColLetter = totalCols <= 26
    ? String.fromCharCode(64 + totalCols)
    : 'A' + String.fromCharCode(64 + totalCols - 26);

  const ws = workbook.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 7 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
  });
  ws.properties.defaultRowHeight = 20;
  ws.columns = columns.map((c) => ({ width: c.width || 16 }));

  // Title
  ws.mergeCells(`A1:${lastColLetter}1`);
  ws.getCell('A1').value = titulo;
  ws.getCell('A1').font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  ws.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
  ws.getRow(1).height = 26;

  // Timestamp
  ws.mergeCells(`A2:${lastColLetter}2`);
  ws.getCell('A2').value = `Generado el ${new Date().toLocaleString('es-PE')}${usuario ? ` | Usuario: ${usuario}` : ''}`;
  ws.getCell('A2').font = { italic: true, size: 10, color: { argb: 'FF475467' } };
  ws.getCell('A2').alignment = { horizontal: 'center' };
  ws.getRow(2).height = 18;

  // Filter
  ws.mergeCells(`A3:${lastColLetter}3`);
  ws.getCell('A3').value = `Filtro: ${searchText || 'Todos los registros'}`;
  ws.getCell('A3').font = { size: 10, color: { argb: 'FF344054' } };
  ws.getCell('A3').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
  ws.getCell('A3').alignment = { horizontal: 'left' };

  // KPIs — row 5
  const totalRegistros = rows.length;
  const activos = rows.filter((r) => {
    const estados = columns.filter((c) => c.isEstado);
    if (!estados.length) return false;
    const val = String(r[estados[0].key] ?? '').toLowerCase();
    return val === 'activo' || val === 'sí' || val === 'si' || val === 'true' || val === '1';
  }).length;
  const inactivos = totalRegistros - activos;
  const hasEstado = columns.some((c) => c.isEstado);
  const sumTotalCols = columns.filter((c) => c.sumTotal);

  // KPI position: each tile occupies 2 cols + 1 gap; startCol = 1 + idx*3
  let kpiIdx = 0;
  const addKpiAuto = (label, value, fill) => {
    const s = 1 + kpiIdx * 3;
    const c1 = colLetter(s); const c2 = colLetter(s + 1);
    addKpi(ws, `${c1}5:${c2}5`, `${c1}6:${c2}7`, label, value, fill);
    kpiIdx++;
  };

  addKpiAuto('Total registros', totalRegistros, 'FF175CD3');
  if (hasEstado) {
    addKpiAuto('Activos', activos, 'FF027A48');
    addKpiAuto('Inactivos', inactivos, 'FFDC2626');
  }
  sumTotalCols.forEach((col) => {
    const sum = rows.reduce((acc, r) => acc + (parseFloat(r[col.key]) || 0), 0);
    const cell = ws.getCell(`${colLetter(1 + kpiIdx * 3)}6`);
    addKpiAuto(`Total ${col.label}`, sum, 'FF7C3AED');
    cell.numFmt = '#,##0.00';
  });

  // Header row 9
  const HEADER_ROW = 9;
  ws.getRow(HEADER_ROW).values = columns.map((c) => c.label);
  styleHeaderRow(ws.getRow(HEADER_ROW));

  // Data rows
  rows.forEach((row, index) => {
    const excelRow = ws.getRow(HEADER_ROW + 1 + index);
    excelRow.values = columns.map((c) => row[c.key] ?? '');
    excelRow.height = 20;
    styleBodyRow(excelRow, index);

    // Format numeric and estado columns
    columns.forEach((c, ci) => {
      const cell = excelRow.getCell(ci + 1);
      if (c.isEstado) colorEstado(cell);
      if (c.sumTotal && typeof cell.value === 'number') {
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
    });
  });

  // AutoFilter
  ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: totalCols } };

  // Total row
  const lastDataRow = HEADER_ROW + rows.length;
  const totRow = ws.getRow(lastDataRow + 1);
  totRow.getCell(1).value = `Total: ${totalRegistros} registros`;
  columns.forEach((c, ci) => {
    if (c.sumTotal) {
      const sum = rows.reduce((acc, r) => acc + (parseFloat(r[c.key]) || 0), 0);
      const cell = totRow.getCell(ci + 1);
      cell.value = sum;
      cell.numFmt = '#,##0.00';
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }
  });
  totRow.height = 22;
  totRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FF123247' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2F8' } };
    setThinBorder(cell, 'FFB9C8D6');
  });
}

function createDataSheet(workbook, opts) {
  const { sheetName, tableName, columns, rows } = opts;
  const dataSheetName = `Datos ${sheetName}`.substring(0, 31); // Excel limit

  const ws = workbook.addWorksheet(dataSheetName, { views: [{ state: 'frozen', ySplit: 1 }] });

  ws.addTable({
    name: tableName,
    ref: 'A1',
    headerRow: true,
    totalsRow: true,
    style: { theme: 'TableStyleMedium2', showRowStripes: true },
    columns: columns.map((c, i) => ({
      name: c.tableKey || c.label.replace(/[^a-zA-Z0-9]/g, ''),
      totalsRowLabel: i === 0 ? 'Totales' : undefined,
      totalsRowFunction: c.sumTotal ? 'sum' : undefined
    })),
    rows: rows.map((row) => columns.map((c) => row[c.key] ?? ''))
  });

  ws.columns = columns.map((c) => ({ width: c.width || 16 }));
  ws.getRow(1).font = { bold: true };
  ws.getCell(`A${rows.length + 4}`).value = 'Base lista para filtros y tabla dinámica en Excel.';
  ws.getCell(`A${rows.length + 4}`).font = { italic: true, color: { argb: 'FF667085' } };
}

// ── Hoja agrupada: cabecera + su detalle debajo ──────────────────────────────

// Tabla plana: una fila por línea de detalle, con las columnas de cabecera
// repetidas en cada fila (sin agrupar, sin filas de separación) — ordenada por
// documento tal como vienen los grupos. Ej.: si la factura 100 tiene 3 ítems,
// salen 3 filas seguidas, cada una con la cabecera de la factura 100 repetida.
function createGroupedDetailSheet(workbook, detalleAgrupado) {
  const { sheetName, cabeceraCols, detalleCols, grupos } = detalleAgrupado;
  const ws = workbook.addWorksheet(sheetName.substring(0, 31), {
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const columnasTodas = [...cabeceraCols, ...detalleCols];
  ws.columns = columnasTodas.map((c) => ({ width: c.width || 16 }));

  const hRow = ws.getRow(1);
  hRow.values = columnasTodas.map((c) => c.label);
  styleHeaderRow(hRow);

  let rowIdx = 2;
  let rowIndexForStripe = 0;
  grupos.forEach((grupo) => {
    const lineas = grupo.detalle.length ? grupo.detalle : [{}]; // documento sin detalle: igual sale 1 fila con la cabecera
    lineas.forEach((det) => {
      const dRow = ws.getRow(rowIdx);
      dRow.values = [
        ...cabeceraCols.map((c) => grupo.cabecera[c.key] ?? ''),
        ...detalleCols.map((c) => det[c.key] ?? ''),
      ];
      styleBodyRow(dRow, rowIndexForStripe);
      columnasTodas.forEach((c, ci) => {
        const cell = dRow.getCell(ci + 1);
        if ((c.isNumeric || c.sumTotal) && typeof cell.value === 'number') {
          cell.numFmt = '#,##0.00';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
        if (c.isEstado) colorEstado(cell);
      });
      rowIdx++;
      rowIndexForStripe++;
    });
  });
}

// ── Hoja seccionada: mismas columnas que la hoja principal, agrupada por un campo ──

function createSectionedSheet(workbook, agrupadoPorCampo) {
  const { sheetName, columns, grupos } = agrupadoPorCampo;
  const ws = workbook.addWorksheet(sheetName.substring(0, 31), {
    views: [{ state: 'frozen', ySplit: 0 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.columns = columns.map((c) => ({ width: c.width || 16 }));
  const totalCols = columns.length;
  const lastColLetter = colLetter(totalCols);

  let rowIdx = 1;
  grupos.forEach((grupo) => {
    // Título de sección (una fila, combinada en todas las columnas)
    ws.mergeCells(`A${rowIdx}:${lastColLetter}${rowIdx}`);
    const sumTotalCols = columns.filter((c) => c.sumTotal);
    const sumaGrupo = sumTotalCols.length
      ? sumTotalCols.map((c) => {
          const s = grupo.rows.reduce((acc, r) => acc + (parseFloat(r[c.key]) || 0), 0);
          return `${c.label}: ${s.toFixed(2)}`;
        }).join('  |  ')
      : '';
    const tCell = ws.getCell(`A${rowIdx}`);
    tCell.value = `${grupo.titulo}  (${grupo.rows.length} documentos)${sumaGrupo ? '  —  ' + sumaGrupo : ''}`;
    tCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    tCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF123247' } };
    tCell.alignment = { vertical: 'middle', horizontal: 'left' };
    ws.getRow(rowIdx).height = 22;
    rowIdx++;

    // Encabezado de columnas
    const hRow = ws.getRow(rowIdx);
    hRow.values = columns.map((c) => c.label);
    styleHeaderRow(hRow);
    rowIdx++;

    // Filas de datos
    grupo.rows.forEach((row, index) => {
      const dRow = ws.getRow(rowIdx);
      dRow.values = columns.map((c) => row[c.key] ?? '');
      dRow.height = 20;
      styleBodyRow(dRow, index);
      columns.forEach((c, ci) => {
        const cell = dRow.getCell(ci + 1);
        if (c.isEstado) colorEstado(cell);
        if (c.sumTotal && typeof cell.value === 'number') {
          cell.numFmt = '#,##0.00';
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
      });
      rowIdx++;
    });

    rowIdx++; // fila en blanco de separación entre secciones
  });
}

// ── Hoja plana adicional: mismo estilo que la principal, sin KPIs ni fila total,
//    una fila por registro tal cual viene — usada cuando cada fila ya representa
//    una unidad lógica distinta a la hoja principal (ej. una cadena de documentos
//    en vez de un documento suelto). ────────────────────────────────────────────

function createFlatSheet(workbook, flatSheet) {
  const { sheetName, columns, rows } = flatSheet;
  const ws = workbook.addWorksheet(sheetName.substring(0, 31), {
    views: [{ state: 'frozen', ySplit: 1 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  ws.columns = columns.map((c) => ({ width: c.width || 16 }));

  const hRow = ws.getRow(1);
  hRow.values = columns.map((c) => c.label);
  styleHeaderRow(hRow);

  rows.forEach((row, index) => {
    const dRow = ws.getRow(index + 2);
    // Las columnas isLink traen un objeto { text, target } en vez de un valor
    // plano — se dejan vacías aquí y se llenan aparte como hipervínculo.
    dRow.values = columns.map((c) => (c.isLink ? '' : row[c.key] ?? ''));
    dRow.height = 20;
    styleBodyRow(dRow, index);
    columns.forEach((c, ci) => {
      const cell = dRow.getCell(ci + 1);
      if (c.isLink) {
        const link = row[c.key];
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if (link && link.target) {
          cell.value = { text: link.text || 'Ver detalle', hyperlink: link.target };
          cell.font = { color: { argb: 'FF1D4ED8' }, underline: true, bold: true };
        } else {
          cell.value = (link && link.text) || '—';
          cell.font = { color: { argb: 'FF98A2B3' } };
        }
        return;
      }
      if (c.isEstado) colorEstado(cell);
      if ((c.isNumeric || c.sumTotal) && typeof cell.value === 'number') {
        cell.numFmt = '#,##0.00';
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
    });
  });

  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
}

// ── Public export ──────────────────────────────────────────────────────────────

/**
 * @param {Object}   options
 * @param {string}   options.titulo       - Report title
 * @param {string}   options.sheetName    - Main sheet name
 * @param {string}   options.tableName    - Excel table name (no spaces)
 * @param {string}   options.fileName     - Base filename (no extension)
 * @param {Array}    options.columns      - Column definitions:
 *   { key, label, width?, isEstado?, sumTotal?, tableKey? }
 * @param {Array}    options.rows         - Pre-mapped data rows (plain objects)
 * @param {string}   options.searchText   - Current search/filter text
 * @param {string}   options.usuario      - Logged-in username
 * @param {Object}   [options.detalleAgrupado] - Hoja extra opcional cabecera+detalle:
 *   { sheetName, cabeceraCols: [{key,label,width}], detalleCols: [{key,label,width,isNumeric}],
 *     grupos: [{ cabecera: {...}, detalle: [{...}] }] }
 * @param {Object}   [options.agrupadoPorCampo] - Hoja extra opcional: mismas columnas que
 *   la hoja principal, seccionada por un campo (ej. condición de pago):
 *   { sheetName, columns: [...], grupos: [{ titulo, rows: [...] }] }
 * @param {Object}   [options.flatSheet] - Hoja extra opcional, tabla plana simple
 *   (sin KPIs ni fila de totales): { sheetName, columns: [...], rows: [...] }.
 *   Una columna con `isLink: true` espera en cada fila un valor
 *   { text, target } — target es un hipervínculo interno tipo
 *   "#'Nombre Hoja'!A5" (salta a esa celda al hacer clic); si no hay target
 *   se muestra solo el texto (o "—") sin estilo de enlace.
 */
export async function exportGenericWorkbook(options) {
  const ExcelJSModule = await import('exceljs');
  const ExcelJS = ExcelJSModule.default || ExcelJSModule;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'InsightSoft';
  workbook.created = new Date();

  createMainSheet(workbook, options);
  if (!options.skipDataSheet) createDataSheet(workbook, options);
  if (options.detalleAgrupado && Array.isArray(options.detalleAgrupado.grupos)) {
    createGroupedDetailSheet(workbook, options.detalleAgrupado);
  }
  if (options.agrupadoPorCampo && Array.isArray(options.agrupadoPorCampo.grupos)) {
    createSectionedSheet(workbook, options.agrupadoPorCampo);
  }
  if (options.flatSheet && Array.isArray(options.flatSheet.rows)) {
    createFlatSheet(workbook, options.flatSheet);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const timestamp = new Date().toISOString().slice(0, 10);
  saveAs(new Blob([buffer], { type: EXCEL_MIME }), `${options.fileName}-${timestamp}.xlsx`);
}
